'use client'

import { useRef, useState, useEffect } from 'react'
import { formatPrice } from '@/lib/formatters'

interface Props {
  currentPrice: number
  support: number[]
  resistance: number[]
  recentPrices: [number, number][] // [timestamp, price] — last 30 days
}

export function SupportResistance({ currentPrice, support, resistance, recentPrices }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(700)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    setWidth(el.offsetWidth)
    const ro = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const HEIGHT = 360
  const CHART_PCT = width < 640 ? 0.45 : 0.80
  const chartWidth = width * CHART_PCT

  const pricePath = recentPrices.map((p) => p[1])
  const allLevels = [...support, ...resistance, currentPrice, ...pricePath]
  const min = Math.min(...allLevels) * 0.98
  const max = Math.max(...allLevels) * 1.02
  const range = max - min

  const yPos = (p: number) => ((max - p) / range) * 100
  const yPx = (p: number) => ((max - p) / range) * HEIGHT

  const pctDiff = (target: number) => {
    const pct = ((target - currentPrice) / currentPrice) * 100
    return (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%'
  }

  // SVG path
  const pathD = pricePath
    .map((p, i) => {
      const x = (i / (pricePath.length - 1)) * chartWidth
      const y = yPx(p)
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  const areaD = pathD + ` L${chartWidth.toFixed(1)},${HEIGHT} L0,${HEIGHT} Z`

  const levels = [
    { label: 'R3', price: resistance[2], side: 'resistance' as const },
    { label: 'R2', price: resistance[1], side: 'resistance' as const },
    { label: 'R1', price: resistance[0], side: 'resistance' as const },
    { label: 'S1', price: support[0], side: 'support' as const },
    { label: 'S2', price: support[1], side: 'support' as const },
    { label: 'S3', price: support[2], side: 'support' as const },
  ]

  return (
    <div className="border border-zinc-800 p-5" ref={containerRef}>
      <div className="relative" style={{ height: HEIGHT }}>
        {/* Red zone above */}
        <div
          className="absolute left-0 right-0"
          style={{
            top: `${yPos(max)}%`,
            height: `${yPos(currentPrice) - yPos(max)}%`,
            background: 'linear-gradient(to bottom, rgba(239,68,68,0.05), rgba(239,68,68,0.01))',
          }}
        />
        {/* Green zone below */}
        <div
          className="absolute left-0 right-0"
          style={{
            top: `${yPos(currentPrice)}%`,
            height: `${yPos(min) - yPos(currentPrice)}%`,
            background: 'linear-gradient(to bottom, rgba(34,197,94,0.01), rgba(34,197,94,0.05))',
          }}
        />

        {/* SVG price path */}
        <svg
          className="absolute top-0 left-0 pointer-events-none"
          width={chartWidth}
          height={HEIGHT}
          style={{ overflow: 'visible' }}
        >
          <defs>
            <linearGradient id="srGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaD} fill="url(#srGrad)" />
          <path d={pathD} fill="none" stroke="#3b82f6" strokeWidth="1.5" opacity="0.5" />
          <circle cx={chartWidth} cy={yPx(currentPrice)} r="3" fill="#3b82f6" />
        </svg>

        {/* Resistance lines */}
        {levels
          .filter((l) => l.side === 'resistance')
          .map((l) => (
            <div
              key={l.label}
              className="absolute left-0 right-0 flex items-center"
              style={{ top: `${yPos(l.price)}%` }}
            >
              <div className="flex-1 border-t border-dashed border-red-500/30" />
              <div className="flex items-center gap-2 ml-2 shrink-0">
                <span className="text-red-500 text-xs font-medium">{l.label}</span>
                <span className="text-zinc-400 text-xs tabular-nums text-right">{formatPrice(l.price)}</span>
                <span className="text-red-500/80 text-xs tabular-nums text-right">{pctDiff(l.price)}</span>
              </div>
            </div>
          ))}

        {/* Current price */}
        <div
          className="absolute left-0 right-0 flex items-center z-10"
          style={{ top: `${yPos(currentPrice)}%` }}
        >
          <div className="flex-1 border-t border-white/80" />
          <div className="flex items-center gap-2 ml-2 shrink-0">
            <span className="text-white text-sm font-semibold">Now</span>
            <span className="text-white text-sm font-semibold tabular-nums text-right">{formatPrice(currentPrice)}</span>
            <span className="text-zinc-500 text-xs tabular-nums text-right">0.0%</span>
          </div>
        </div>

        {/* Support lines */}
        {levels
          .filter((l) => l.side === 'support')
          .map((l) => (
            <div
              key={l.label}
              className="absolute left-0 right-0 flex items-center"
              style={{ top: `${yPos(l.price)}%` }}
            >
              <div className="flex-1 border-t border-dashed border-green-500/30" />
              <div className="flex items-center gap-2 ml-2 shrink-0">
                <span className="text-green-500 text-xs font-medium">{l.label}</span>
                <span className="text-zinc-400 text-xs tabular-nums text-right">{formatPrice(l.price)}</span>
                <span className="text-green-500/80 text-xs tabular-nums text-right">{pctDiff(l.price)}</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}